import React, { useState } from "react"
import { useHistory } from "react-router-dom"
import {
	Avatar,
	Box,
	Chip,
	Grid,
	IconButton,
	List,
	ListItem,
	ListItemText,
	Paper,
	Popover,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Tooltip,
	Typography
} from "@material-ui/core"

import {
	Person,
	DeleteOutline as TrashIcon,
	LockOutlined as BlockContactIcon,
	LockOpenOutlined as UnblockContactIcon,
	MoreVert as MoreOptionsIcon
} from "@material-ui/icons"
import {
	ReactComponent as ChatBotIcon
} from "@/assets/icons/chat_bot.svg"

import ContactAction from "@/components/ContactAction"
import {
	Divider,
	Loading,
	LoadingOverlay,
	Notification,
	Portlet,
	TypographyListGroup
} from "@/components"
import Breadcrumb from "@/components/BreadcrumbNew"
import ContactGroupPermissionGate from "@/components/PermissionGate/ContactGroupPermissionGate"

import { Tag } from "@/protocols/tag"
import {
	Client,
	ClientCatalogPagination
} from "@/protocols/clientCatalog"

import ApiService from "@/services/Api"
import ErrorHandlerService from "@/services/ErrorHandler"
import TagService from "@/services/Tag"

import useDidMount from "@/hooks/useDidMount"
import useValidation, { ErrorType } from "@/hooks/useValidation"
import useDebounce from "@/hooks/useDebounce"
import useQueryFilters from "@/hooks/useQueryFilters"
import useSubscriptionLimits from "@/hooks/useSubscriptionLimits"

import ClientProfileInfo, { OnDataChangeFn } from "@/pages/Admin/ClientCatalog/ClientProfileInfo"
import ClientCatalogQualifyMetrics from "@/pages/Admin/ClientCatalog/ClientCatalogQualifyMetrics"

import { isSmallScreen } from "@/utils/checkDevice"
import { convertRGBAObjectToString } from "@/utils/color"
import { getRowsLabel } from "@/utils/table"
import { DEFAULT_WHERE_DATA } from "@/utils/pagination"
import { getPhoneContact, getPhoneNumber, isLidContact } from "@/utils/contact"

import ClientCatalogTableSkeleton from "@/skeletons/Admin/ClientCatalogTable"

import useStyles from "@/pages/Admin/ClientCatalog/styles"
import useCustomStyles from "@/styles/custom"
import ClientManageContacts from "@/pages/Admin/ClientCatalog/ClientManageFilters"
import ClientManageContactsHeader from "@/pages/Admin/ClientCatalog/ClientManageContactsHeader"
import { BaseContactsFiltersType } from "@/protocols/ClientCatalogFilterProtocol"
import DisableContactDialog from "@/pages/Admin/ClientCatalog/DisableContactDialog"
import { useGlobalStateStore } from "@/store/GlobalState"
import BlockContactDialog from "@/pages/Admin/ClientCatalog/BlockContactDialog"
import UnblockContactDialog from "@/pages/Admin/ClientCatalog/UnblockContactDialog"
import useSocket from "@/hooks/useSocket"
import useContactDisplay from "@/hooks/useContactDisplay"
import { colors } from "@/styles/colors"
import LidContactWarning from "@/components/LidContactWarning"
import clsx from "clsx"
import { MetaFreeEntryPointWindowIcon } from "@/assets/icons"
import { WABAChannelChatCommunicationPermissionStatusOnFilter } from "@/@integrations/WABA/protocols/wabaChannelChatCommunicationPermission"
import { WABAChannelChatFreeEntryPointWindowStatusOnFilter } from "@/@integrations/WABA/protocols/wabaChannelChatFreeEntryPointWindow"

const wabaCommunicationPermissionStatusToLabel: Record<WABAChannelChatCommunicationPermissionStatusOnFilter, string> = {
	active: "Ativa",
	paused: "Inativa",
	expiring: "Prestes a encerrar"
}

const metaFreeEntryPointWindowStatusToLabel: Record<WABAChannelChatFreeEntryPointWindowStatusOnFilter, string> = {
	active: "Ativo",
	paused: "Inativo",
	expiring: "Prestes a encerrar"
}

export type ClientCatalogWhereData = {
	page: number
	rowsPerPage: number
	search?: string
	searchTags?: string[]
	contactsFilters?: BaseContactsFiltersType
}
type ClientContactActionData = {
	clientId: number
	contactId?: number
	name?: string
}

export type ContactEventPayload = {
	id: number
}

const ClientCatalogPage: React.FC = () => {
	const [loadingClientCatalog, setLoadingClientCatalog] = useState(true)
	const [clientCatalog, setClientCatalog] = useState<ClientCatalogPagination>({} as ClientCatalogPagination)
	const [whereData, setWhereData] = useState<ClientCatalogWhereData>(DEFAULT_WHERE_DATA)
	const [customerTags, setCustomerTags] = useState<Tag[]>([])
	const { triggerValidation } = useValidation()
	const queryFilter = useQueryFilters()
	const history = useHistory()
	const [contactActionData, setContactActionData] = useState<ClientContactActionData>()
	const [openContactAction, setOpenContactAction] = useState<boolean>(false)
	const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null)
	const [popoverSelectedClientId, setPopoverSelectedClientId] = useState<number | null>(null)

	const contactActionSubscriptionData = useSubscriptionLimits("permission", "bulk_contact_action")
	const mustBlockContactAction = contactActionSubscriptionData.mustBlock
	const contactsCount = clientCatalog.rows?.length > 0

	const globalStateStore = useGlobalStateStore()
	const socket = useSocket()
	const { getDisplayName, getDisplayPhoneNumber } = useContactDisplay()

	const isWabaChannel = globalStateStore.currentChannel?.channelType === "waba"

	const setupInitialContactNameSearch = async (): Promise<string | null> => {
		const nickNameSearch = queryFilter.getQueryParamSearch("nickname")
		history.replace({ search: queryFilter.query.toString() })

		return nickNameSearch
	}

	const getAndUpdateClientCatalogData = async (newWhereData?: Partial<ClientCatalogWhereData>) => {
		setLoadingClientCatalog(true)
		try {
			const whereParams = newWhereData

			const nickNameSearch = await setupInitialContactNameSearch()

			const updatedWhereData: ClientCatalogWhereData = {
				...whereData,
				search: nickNameSearch || whereData?.search || ""
			}

			setWhereData({
				...updatedWhereData,
				...(whereParams && whereParams)
			})

			const { data } = await ApiService.get("/clients", {
				params: {
					...updatedWhereData,
					...whereParams,
					contactType: "user"
				}
			})

			setClientCatalog({
				clientCatalog: data.clientCatalog,
				count: data.count,
				rows: data.rows
			})
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)

			const isSearchTextHaveLessFourCharacters = (error as ErrorType)?.response?.data?.codeMessages?.search === "number_of_characters_less_than_three"

			if (isSearchTextHaveLessFourCharacters) {
				Notification.warning({
					message: "Você deve inserir mais de 3 caracteres."
				})
			}

			triggerValidation(error as ErrorType)
		}

		setLoadingClientCatalog(false)
	}

	const onDataChange: OnDataChangeFn = (id, data) => {
		const updatedClients = clientCatalog?.rows?.map(client => {
			if (client.id === id) {
				if (data.contacts?.email) {
					client.contacts = client.contacts?.map((contact) => {
						if (contact.channel_type === "email") {
							contact.data.email = data.contacts?.email as string
						}
						return contact
					})
				}
				if ("optIn" in data) {
					client.accepts_automatic_messages = data.optIn as boolean
				}
				if ("nickname" in data) {
					client.nickname = data.nickname as string
				}
				if ("tags" in data) {
					client.tags = data.tags
				}
			}

			return client
		})
		const updatedClientCatalog = {
			...clientCatalog,
			rows: updatedClients
		} as ClientCatalogPagination

		setClientCatalog(updatedClientCatalog)
	}
	const getAndUpdateCustomerTags = async () => {
		const data = await TagService.getTags()

		if (data) {
			setCustomerTags(data)
		}
	}
	const handleWhereDataChange = (newData: Partial<ClientCatalogWhereData>) => {
		setWhereData((currentState) => (
			{
				...currentState,
				...newData
			}))
	}
	const handlePageChange = async (event: unknown, page: number) => {
		handleWhereDataChange({
			page
		})

		await getAndUpdateClientCatalogData({ page })
	}

	const handleChangeRowsPerPage = async (rowsPerPage: number) => {
		handleWhereDataChange({
			rowsPerPage
		})
		await getAndUpdateClientCatalogData({ rowsPerPage })
	}

	const openSendContactToBot = async (client: Client) => {
		const contact = getPhoneContact(client)

		setOpenContactAction(true)
		setContactActionData({
			clientId: client.id,
			contactId: contact?.id,
			name: client.name || client.nickname
		})
	}

	useDebounce(
		async () => {
			getAndUpdateClientCatalogData({ search: whereData?.search })
		},
		whereData.search,
		2250
	)

	const handleRemoveSelectedContactFilters = async () => {
		setWhereData({ ...whereData, contactsFilters: {} as BaseContactsFiltersType })

		await getAndUpdateClientCatalogData({ contactsFilters: undefined })
	}

	const handleOpenMoreOptionsPopover = (
		event: React.MouseEvent<HTMLButtonElement>,
		clientId: number
	) => {
		setPopoverAnchor(event.currentTarget)
		setPopoverSelectedClientId(clientId)
	}

	const handleCloseMoreOptionsPopover = () => {
		setPopoverAnchor(null)
		setPopoverSelectedClientId(null)
	}

	function updateContactBlockedStatus (
		clientCatalog: ClientCatalogPagination,
		id: number,
		isBlocked: boolean
	): ClientCatalogPagination {
		const updatedClients = clientCatalog?.rows?.map(client => {
			const isTargetClient = client.id === id

			if (!isTargetClient) return client

			const updatedContacts = client.contacts?.map(contact => {
				const isWabaChannel = contact.channel_type === "waba"
				const contactData = contact.data

				if (!isWabaChannel) return contact

				const updatedData = { ...contactData, is_blocked: isBlocked }

				return { ...contact, data: updatedData }
			})

			return { ...client, contacts: updatedContacts }
		})

		return {
			...clientCatalog,
			count: clientCatalog.count,
			rows: updatedClients
		}
	}

	const setupWabaContactEvents = async () => {
		socket.onBlockContact((payload: ContactEventPayload) => {
			setClientCatalog(prev => updateContactBlockedStatus(prev, payload.id, true))
			Notification.success({ message: "Contato bloqueado" })
		})

		socket.onUnblockContact((payload: ContactEventPayload) => {
			setClientCatalog(prev => updateContactBlockedStatus(prev, payload.id, false))
			Notification.success({ message: "Contato desbloqueado" })
		})
	}

	useDidMount(() => {
		getAndUpdateClientCatalogData()
		getAndUpdateCustomerTags()
		setupWabaContactEvents()
	})

	const classes = useStyles()
	const customClasses = useCustomStyles()
	const inboxChannelId = globalStateStore.currentChannel?.id as number

	const isPopoverOpen = !!popoverAnchor

	return (
		<Grid item>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Breadcrumb
						data={[
							{ name: "Contatos", pathname: "/admin/client-catalog" }
						]}
					/>
				</Grid>
				<Grid item>
					<ClientManageContactsHeader
						selectedContactsFilter={whereData?.contactsFilters}
						setClientCatalog={setClientCatalog} />
				</Grid>
				<Grid item xs={12}>
					<ClientCatalogQualifyMetrics />
				</Grid>
				<Grid item>
					<Portlet
						elevation={1}
					>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<ClientManageContacts
									getAndUpdateClientCatalogData={getAndUpdateClientCatalogData}
									setWhereData={setWhereData}
									whereData={whereData}
									hasContactsOnCurrentFilter={Boolean(contactsCount)}
								/>
							</Grid>

							{isWabaChannel && (
								<Grid item xs={12}>
									<Grid container alignItems="center" spacing={1} className={classes.communicationPermissionLegendContainer}>
										<Grid item>
											<Typography variant="caption" className={classes.communicationPermissionLegendLabel}>
												Permissão de comunicação WABA:
											</Typography>
										</Grid>
										<Grid item>
											<Grid container alignItems="center" spacing={1}>
												<Grid item>
													<Grid container alignItems="center" spacing={1}>
														<Grid item>
															<Box className={clsx(classes.wabaCommunicationPermissionDot, classes.wabaCommunicationPermissionDotActive)} />
														</Grid>
														<Grid item>
															<Typography variant="caption">Ativa</Typography>
														</Grid>
													</Grid>
												</Grid>
												<Grid item>
													<Grid container alignItems="center" spacing={1}>
														<Grid item>
															<Box className={clsx(classes.wabaCommunicationPermissionDot, classes.wabaCommunicationPermissionDotExpiring)} />
														</Grid>
														<Grid item>
															<Typography variant="caption">Prestes a encerrar</Typography>
														</Grid>
													</Grid>
												</Grid>
												<Grid item>
													<Grid container alignItems="center" spacing={1}>
														<Grid item>
															<Box className={clsx(classes.wabaCommunicationPermissionDot, classes.wabaCommunicationPermissionDotPaused)} />
														</Grid>
														<Grid item>
															<Typography variant="caption">Inativa</Typography>
														</Grid>
													</Grid>
												</Grid>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							)}

							<Grid item xs={12}>
								<Loading
									customLoadingElement={<ClientCatalogTableSkeleton />}
									loading={loadingClientCatalog}
								>
									{
										clientCatalog.count > 0 ? (
											<LoadingOverlay
												loading={loadingClientCatalog}
											>
												<Paper className={classes.paper}>
													<TableContainer>
														<Table stickyHeader size="small">
															<TableHead>
																<TableRow>
																	<TableCell
																		width="300px"
																	>
																		Cliente
																	</TableCell>

																	<TableCell
																		width="220px"
																	>
																		WhatsApp
																	</TableCell>

																	<TableCell
																	>
																		Tags
																	</TableCell>
																	<TableCell
																		width="180px"
																		className={classes.automaticMessagesHeaderCell}
																	>
																		Receber mensagem automática?
																	</TableCell>

																	<TableCell
																		className={classes.actionsHeaderCell}
																	/>
																</TableRow>
															</TableHead>

															<TableBody
																className={customClasses.reportTableBodyText}
															>
																{clientCatalog.rows.map(client => {
																	return (
																		<TableRow key={client.id} tabIndex={-1}>
																			<TableCell>
																				<Grid container justify="flex-start" alignItems="center" >
																					<Grid item>
																						<Avatar src={client.picture_url} />
																					</Grid>

																					{isWabaChannel && (
																						<>
																							<Divider size={isSmallScreen ? 0.5 : 1} orientation={isSmallScreen ? "horizontal" : "vertical"} />
																							<Grid item>
																								<Tooltip title={`Permissão de comunicação WABA: ${wabaCommunicationPermissionStatusToLabel[client.wabaCommunicationPermissionStatus ?? "paused"]}`}>
																									<Box
																										className={clsx(classes.wabaCommunicationPermissionDot, {
																											[classes.wabaCommunicationPermissionDotActive]: client.wabaCommunicationPermissionStatus === "active",
																											[classes.wabaCommunicationPermissionDotExpiring]: client.wabaCommunicationPermissionStatus === "expiring",
																											[classes.wabaCommunicationPermissionDotPaused]: client.wabaCommunicationPermissionStatus === "paused" || !client.wabaCommunicationPermissionStatus
																										})}
																									/>
																								</Tooltip>
																							</Grid>
																						</>
																					)}

																					<Divider size={isSmallScreen ? 1 : 2.5} orientation={isSmallScreen ? "horizontal" : "vertical"} />

																					<Grid item xs>
																						<Grid container alignItems="center" wrap="nowrap" spacing={1}>
																							<Grid item className={classes.clientNameMinWidth}>
																								<TypographyListGroup>
																									<Typography variant="h4">
																										{getDisplayName(client.nickname, getPhoneNumber(client))}
																									</Typography>

																									<Grid container alignItems="center">
																										<Grid item className={classes.clientNameMinWidth}>
																											<Grid container direction="column">
																												<Grid item className={classes.clientNameMinWidth}>
																													<Grid container alignItems="center">
																														<Grid item className={classes.clientNameMinWidth}>
																															<Typography variant="h4" className={classes.clientNicknameOverflow}>
																																{getDisplayName(client.nickname, getPhoneNumber(client))}
																															</Typography>
																														</Grid>
																														{isLidContact(client) && (
																															<Grid item className={classes.lidContactWarningItem}>
																																<LidContactWarning />
																															</Grid>
																														)}
																													</Grid>
																												</Grid>

																												<Grid item className={classes.clientNameOverflowContainer}>
																													<Typography className={classes.clientNameSecondaryText}>
																													~{getDisplayName(client.name, getPhoneNumber(client))}
																													</Typography>
																												</Grid>
																											</Grid>
																										</Grid>
																									</Grid>
																								</TypographyListGroup>
																							</Grid>

																							{isWabaChannel && client.wabaFreeEntryPointWindowStatus && client.wabaFreeEntryPointWindowStatus !== "paused" && (
																								<Grid item className={clsx(classes.metaFreeEntryPointWindowBadge, classes.metaFreeEntryPointWindowBadgeItem, {
																									[classes.metaFreeEntryPointWindowBadgeExpiring]: client.wabaFreeEntryPointWindowStatus === "expiring"
																								})}>
																									<Tooltip title={`Modo Meta: custo reduzido: ${metaFreeEntryPointWindowStatusToLabel[client.wabaFreeEntryPointWindowStatus]}`}>
																										<Grid container alignItems="center">
																											<MetaFreeEntryPointWindowIcon className={classes.metaFreeEntryPointWindowBadgeIcon} />
																											<Typography>7d</Typography>
																										</Grid>
																									</Tooltip>
																								</Grid>
																							)}
																						</Grid>
																					</Grid>
																				</Grid>
																			</TableCell>

																			<TableCell width="185px">
																				<Typography style={{ fontSize: 16 }}>
																					{getDisplayPhoneNumber(getPhoneNumber(client))}
																				</Typography>
																			</TableCell>

																			<TableCell>
																				<Grid container direction="row" wrap="wrap" spacing={1}>
																					{client.tags?.map(tag => {
																						return (
																							<Grid item key={tag.id}>
																								<Tooltip title={tag.name}>
																									<Chip
																										className={classes.tagChip}
																										style={{
																											backgroundColor: convertRGBAObjectToString(tag.color)
																										}}
																										size="small"
																									/>
																								</Tooltip>
																							</Grid>
																						)
																					})}
																				</Grid>
																			</TableCell>

																			<TableCell>
																				<Grid item className={classes.automaticMessagesCellContainer}>
																					<Box className={classes.clientAutomaticMessages} style={{ backgroundColor: client.accepts_automatic_messages ? colors.green[200] : colors.red[100] }}>
																						{client.accepts_automatic_messages ? "Sim" : "Não"}
																					</Box>
																				</Grid>
																			</TableCell>

																			<TableCell>
																				<Grid
																					container
																					justifyContent="flex-end"
																					alignItems="baseline"
																					direction="row"
																				>
																					<Grid item >
																						<ClientProfileInfo
																							customerTags={customerTags}
																							onDataChange={onDataChange}
																							clientId={client.id}
																							onUpdateTags={getAndUpdateCustomerTags}
																						>
																							<IconButton>
																								<Person />
																							</IconButton>
																						</ClientProfileInfo>
																					</Grid>
																					<Grid item>
																						<IconButton onClick={(event) => handleOpenMoreOptionsPopover(event, client.id)}>
																							<MoreOptionsIcon />
																						</IconButton>
																					</Grid>
																				</Grid>
																			</TableCell>
																		</TableRow>
																	)
																})}
															</TableBody>
														</Table>
													</TableContainer>

													<Popover
														open={isPopoverOpen}
														anchorEl={popoverAnchor}
														onClose={handleCloseMoreOptionsPopover}
														transitionDuration={0}
													>
														<List>
															{popoverSelectedClientId && (() => {
																const client = clientCatalog.rows.find(client => client.id === popoverSelectedClientId)
																if (!client) return null

																const wabaContact = client.contacts?.find(contact => contact.channel_type === "waba")
																const isBlocked = wabaContact?.data?.is_blocked
																const canBlockContact = isWabaChannel

																return (
																	<>
																		<Tooltip
																			disableHoverListener={!mustBlockContactAction}
																			title={
																				mustBlockContactAction
																					? "Essa opção está disponível apenas para o plano avançado."
																					: ""
																			}
																		>
																			<div>
																				<ListItem
																					button
																					onClick={() => !mustBlockContactAction && openSendContactToBot(client)}
																					disabled={mustBlockContactAction}
																				>
																					<Grid container alignItems="center" spacing={1}>
																						<Grid item>
																							<ChatBotIcon className={classes.popoverListItemIcon} />
																						</Grid>
																						<Grid item>
																							<ListItemText primary="Enviar contato para um bot" />
																						</Grid>
																					</Grid>
																				</ListItem>
																			</div>
																		</Tooltip>
																		<ContactAction
																			contactAction="single-contact"
																			client={contactActionData}
																			clientDisplayName={client.name || getDisplayName(client.nickname, getPhoneNumber(client))}
																			hideOpenButton
																			open={openContactAction}
																			setOpen={setOpenContactAction}
																		/>

																		{canBlockContact && (
																			<ContactGroupPermissionGate action="block-unblock-waba">
																				{isBlocked ? (
																					<UnblockContactDialog
																						clientId={client.id}
																						inboxChannelId={inboxChannelId}
																						hasTooltip
																					>
																						<ListItem
																							button
																						>
																							<Grid container alignItems="center" spacing={1}>
																								<Grid item>
																									<UnblockContactIcon className={classes.popoverListItemIcon} />
																								</Grid>
																								<Grid item>
																									<ListItemText primary="Desbloquear contato" />
																								</Grid>
																							</Grid>
																						</ListItem>
																					</UnblockContactDialog>
																				) : (
																					<BlockContactDialog
																						clientId={client.id}
																						inboxChannelId={inboxChannelId}
																						hasTooltip
																					>
																						<ListItem
																							button
																						>
																							<Grid container alignItems="center" spacing={1}>
																								<Grid item>
																									<BlockContactIcon className={classes.popoverListItemIcon} />
																								</Grid>
																								<Grid item>
																									<ListItemText primary="Bloquear contato" />
																								</Grid>
																							</Grid>
																						</ListItem>
																					</BlockContactDialog>
																				)}
																			</ContactGroupPermissionGate>
																		)}

																		<ContactGroupPermissionGate action="disable">
																			<DisableContactDialog
																				type="single-contact"
																				disabled={false}
																				client={client}
																				onUpdateContactsList={handleRemoveSelectedContactFilters}
																			>
																				<ListItem
																					button
																				>
																					<Grid container alignItems="center" spacing={1}>
																						<Grid item>
																							<TrashIcon className={classes.popoverListItemIcon} />
																						</Grid>
																						<Grid item>
																							<ListItemText primary="Excluir contato" />
																						</Grid>
																					</Grid>
																				</ListItem>
																			</DisableContactDialog>
																		</ContactGroupPermissionGate>
																	</>
																)
															})()}
														</List>
													</Popover>

													<TablePagination
														rowsPerPageOptions={[20, 50, 100, 200]}
														component="div"
														count={clientCatalog?.count}
														rowsPerPage={whereData.rowsPerPage}
														page={whereData.page}
														onPageChange={handlePageChange}
														onRowsPerPageChange={({ target }) => {
															handlePageChange(target, 0)
															handleChangeRowsPerPage(+target.value)
														}}
														labelRowsPerPage={"Resultados por página:"}
														labelDisplayedRows={tableData => getRowsLabel(tableData, whereData.rowsPerPage)}
													/>
												</Paper>
											</LoadingOverlay>
										) : (
											<>
												<Divider size={3} orientation="horizontal" />
												<Typography align="center" variant="h2">
													Nenhum resultado encontrado
												</Typography>
											</>
										)
									}
								</Loading>
							</Grid>
						</Grid>
					</Portlet >
				</Grid>
			</Grid>
		</Grid>
	)
}

export default ClientCatalogPage
